import { Injectable, OnDestroy, inject } from '@angular/core';
import { httpOptions } from '../_helpers/utils';
import { BehaviorSubject, Observable, map, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AuthenticationService } from './authentication.service';
import { Laboratory } from '../_models/laboratory';
import { environment } from 'src/environments/environment';
import { ErrorLog } from '../_models/error-log';
import { ErrorLogIndexResponse } from '../_models/api-responses';
import { PaginationData } from '../_models/pagination-data.model';
import { takeUntil } from 'rxjs/operators';
import { InformationBannerType } from '../_models/information-banner';

@Injectable({
  providedIn: 'root',
})
export class ErrorLogService implements OnDestroy {
  private readonly httpClient = inject(HttpClient);
  private readonly authenticationService = inject(AuthenticationService);

  httpOptions = httpOptions;
  selectedlaboratoryId: number;

  // Pagination and sort/filter observables for dashboard index view
  private readonly tableSort$ = new BehaviorSubject<{
    sortBy: string;
    sortDirection: string;
  }>(null);
  private readonly tableFilter$ = new BehaviorSubject<string[]>([]);
  private readonly pageNumber$ = new BehaviorSubject<number>(null);
  private readonly pageSize$ = new BehaviorSubject<number>(null);

  private readonly destroy$ = new Subject<void>();

  constructor() {
    // Set up subscription to selected laboratory
    this.authenticationService.selectedLab$
      .pipe(takeUntil(this.destroy$))
      .subscribe((laboratory: Laboratory) => {
        if (
          laboratory &&
          this.selectedlaboratoryId !== laboratory.laboratoryId
        ) {
          this.selectedlaboratoryId = laboratory.laboratoryId;
        }
      });
  }

  onUpdateSort = (sort: { sortBy: string; sortDirection: string }): void => {
    this.tableSort$.next(sort);
  };

  onUpdateFilter = (filter: string[]): void => {
    this.tableFilter$.next(filter);
  };

  onUpdatePageNumber = (pageNumber: number): void => {
    this.pageNumber$.next(pageNumber);
  };

  onUpdatePageSize = (pageSize: number): void => {
    this.pageSize$.next(pageSize);
  };

  public get filter(): string[] {
    return this.tableFilter$.value;
  }

  public get sort(): { sortBy: string; sortDirection: string } {
    return this.tableSort$.value;
  }

  public get paginationCache(): { pageNumber: number; pageSize: number } {
    if (this.pageNumber$.value !== null && this.pageSize$.value !== null) {
      return {
        pageNumber: this.pageNumber$.value,
        pageSize: this.pageSize$.value,
      };
    } else {
      return null;
    }
  }

  getErrorLogs(
    sortBy = 'CreatedAt',
    sortDirection = 'desc',
    pageSize = 5,
    pageNumber = 1,
    search: string = null,
    viewedErrorLogs?: boolean,
  ): Observable<ErrorLogIndexResponse> {
    let url = `${environment.apiUrl}/error-logs/${this.selectedlaboratoryId}?OrderBy=${sortBy}&OrderByDirection=${sortDirection}&PageNumber=${pageNumber}&PageSize=${pageSize}`;
    if (search) {
      url = url + `&search=${search}`;
    }
    if (viewedErrorLogs) {
      url = url + `&viewed=true`;
    } else if (viewedErrorLogs === false) {
      url = url + `&viewed=false`;
    }
    return this.httpClient
      .get<{
        errorLogs: ErrorLog[];
        displayInformationBanner: boolean;
        informationBannerMessage: string;
        informationBannerType: InformationBannerType;
      }>(url, this.httpOptions)
      .pipe(
        map((response) => {
          // Set pagination data
          let paginationData: PaginationData | null = null;
          try {
            const rawPagination = response.headers.get('X-Pagination');
            paginationData = rawPagination
              ? (JSON.parse(rawPagination) as PaginationData)
              : null;
          } catch {
            paginationData = null;
          }
          const body = response.body;
          const errorLogs = body?.errorLogs ?? [];
          return {
            errorLogs,
            paginationData,
            displayInformationBanner: body?.displayInformationBanner ?? false,
            informationBannerMessage: body?.informationBannerMessage ?? '',
            informationBannerType: body?.informationBannerType ?? null,
          };
        }),
      );
  }

  updateErrorLogViewed(
    errorLogIds: number[],
    isViewed?: boolean,
    notes?: string | null,
  ): Observable<ErrorLog> {
    const url = `${environment.apiUrl}/error-logs`;
    return this.httpClient
      .patch<ErrorLog>(url, { errorLogIds, isViewed, notes }, this.httpOptions)
      .pipe(
        map((response) => {
          return response.body;
        }),
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
